import { useEffect, useRef } from 'react'
import { Track, type RemoteTrackPublication, type Room } from 'livekit-client'
import { decodeBudget } from '../services/videoQuality'

// Throttle ResizeObserver fires — small CSS animations / scroll
// reflows can rapid-fire dimension updates that flood the SFU with
// signaling. 200ms is the same window LiveKit's own adaptiveStream
// uses internally, and it's well below human perception.
const RESIZE_DEBOUNCE_MS = 200

// Snap requested dimensions to the publisher's actual SVC ladder so
// the SFU can decide layer cleanly. Without snapping, a tile rendered
// at 873×491 would generate a unique signaling round-trip for every
// pixel the layout shifts. The snap thresholds match the spatial
// layers of an L3T3 VP9 SVC stream (180p / ~540p / 1080p).
function snapDimensions(width: number, height: number): { width: number; height: number } {
  // Take the smaller axis so a portrait tile (rare but possible on a
  // squeezed sidebar) doesn't accidentally request HIGH because its
  // height happens to exceed the threshold.
  const minor = Math.min(width, height)
  if (minor < 360) return { width: 320, height: 180 }
  if (minor < 720) return { width: 960, height: 540 }
  return { width: 1920, height: 1080 }
}

// Pick the dimensions to REQUEST from the SFU for one tile (drives the spatial
// layer). On a busy call with the decode-budget on, every NON-hero tile is
// hard-pinned to 180p regardless of its rendered size — a 7+ camera grid then
// decodes N×180p instead of N×540p/1080p (the decoder-saturation freezes).
// The hero tile (focused / speaker-spotlight) and every tile on a small/quiet
// call fall through to the size-based snap so the person you're looking at
// stays sharp. Pure + exported for unit tests.
export function pickTileDimensions(opts: {
  physW: number
  physH: number
  bigRoomForcedLow: boolean
  isHero: boolean
  budgetOn: boolean
}): { width: number; height: number } {
  const { physW, physH, bigRoomForcedLow, isHero, budgetOn } = opts
  if (budgetOn && bigRoomForcedLow && !isHero) return { width: 320, height: 180 }
  return snapDimensions(physW, physH)
}

/**
 * Drive the SFU's layer choice from the rendered tile size.
 *
 * Why we need this on top of the existing `incomingQuality` cap: with
 * `adaptiveStream: false` LiveKit no longer auto-tracks the <video>
 * element's size, so a tile that grows to fullscreen stays on whichever
 * spatial layer the SFU happened to pick at subscription time
 * (typically L1 / 180p when the tile started small in the grid). The
 * user fullscreens it and it stays at ~250 kbps.
 *
 * `setVideoDimensions` overrides `requestedMaxQuality` (see
 * RemoteTrackPublication.ts:176), so we only call it when the user is
 * on the 'auto' incoming preset. Manual 'medium' / 'low' caps are
 * downlink-protection and must win — Ivan on a tight pipe doesn't want
 * a fullscreen tile to suddenly demand 8 Mbps.
 *
 * @param room - the active LiveKit Room (channel or DM call)
 * @param participantId - identity of the remote participant whose
 *   publication we're rendering, OR null for self-tile (no-op)
 * @param source - 'camera' or 'screen' — which publication on that
 *   participant we're driving
 * @param incomingMode - if anything other than 'auto', this hook is
 *   inert: the user's manual cap takes precedence
 * @param bigRoomForcedLow - the busy-room latch (livekitStore). When true and
 *   the decode-budget flag is on, every NON-hero tile is pinned to 180p.
 * @param isHero - the tile the user is actually looking at (focused /
 *   speaker-spotlight). Exempt from the busy-room 180p pin so it stays sharp.
 */
export function useTileVideoDimensions(opts: {
  room: Room | null
  participantId: string | null
  source: 'camera' | 'screen'
  incomingMode: 'auto' | 'medium' | 'low'
  bigRoomForcedLow: boolean
  isHero: boolean
  elementRef: React.RefObject<HTMLElement | null>
}) {
  const { room, participantId, source, incomingMode, bigRoomForcedLow, isHero, elementRef } = opts
  const lastSnap = useRef<string>('')

  useEffect(() => {
    if (!room || !participantId || incomingMode !== 'auto') return
    const el = elementRef.current
    if (!el) return

    const lkSource = source === 'camera' ? Track.Source.Camera : Track.Source.ScreenShare

    const apply = () => {
      const remote = room.remoteParticipants.get(participantId)
      if (!remote) return
      const pub = remote.getTrackPublication(lkSource) as RemoteTrackPublication | undefined
      if (!pub) return
      const rect = el.getBoundingClientRect()
      // Multiply by devicePixelRatio so the SFU sees the *physical*
      // pixel count of the rendered area. A 950×534 tile on a 2x retina
      // is actually 1900×1068 — enough pixels to merit the 1080p layer.
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
      const physW = Math.round(rect.width * dpr)
      const physH = Math.round(rect.height * dpr)
      if (physW < 16 || physH < 16) return // not laid out yet, ignore
      const snapped = pickTileDimensions({ physW, physH, bigRoomForcedLow, isHero, budgetOn: decodeBudget() })
      const key = `${snapped.width}x${snapped.height}`
      if (lastSnap.current === key) return
      lastSnap.current = key
      try {
        pub.setVideoDimensions(snapped)
      } catch {
        // Publication may have flipped to muted/unsubscribed mid-update.
      }
    }

    // Initial attempt — the publication may not exist yet on first
    // mount; the ResizeObserver path below will retry once layout
    // settles or once the track subscribes. Wrapping in setTimeout 0
    // lets the next microtask inspect a freshly-rendered <video>.
    const initial = setTimeout(apply, 0)

    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    const observer = new ResizeObserver(() => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(apply, RESIZE_DEBOUNCE_MS)
    })
    observer.observe(el)

    // Also listen for the publication arriving late (the tile mounts
    // before TrackSubscribed fires for that peer). The dependency
    // array re-runs this effect when room/participantId change, so
    // we're covered by re-creation; we just need the initial apply +
    // the resize-driven follow-ups.

    return () => {
      clearTimeout(initial)
      if (debounceTimer) clearTimeout(debounceTimer)
      observer.disconnect()
      lastSnap.current = ''
    }
  }, [room, participantId, source, incomingMode, bigRoomForcedLow, isHero, elementRef])
}
